/**
 * One payment against a vendor's opening balance — the page the checker lands
 * on from the bell, with the screenshot, the balance it reduces, and the
 * Approve / Return / Reject buttons. See OpeningLiabilities.tsx and 0179.
 */
import { useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { ArrowLeft } from 'lucide-react'
import {
  Alert, Card, Confirm, EmptyState, Loading, StatusPill, useToast,
} from '@/components/ui'
import {
  AttachmentCollector, AttachmentList, AuditTrail, NotPermitted, queueAttachments,
  type PendingAttachment,
} from '@/components/docs/shared'
import { useAuth } from '@/lib/auth'
import { can } from '@/lib/domain'
import { dbMessage, usePeople, useQuery } from '@/lib/data'
import { supabase } from '@/lib/supabase'
import { dateTime, ddmmyyyy, rupees } from '@/lib/format'
import { OpeningPaymentForm, useOpeningAsAt } from './OpeningLiabilities'

type Act = 'SUBMIT' | 'WITHDRAW' | 'APPROVE' | 'RETURN' | 'REJECT' | 'REVERSE'

const ACT: Record<Act, { title: string; label: string; tone: 'primary' | 'danger' | 'warning' | 'success'; reason: boolean; msg: string }> = {
  SUBMIT:   { title: 'Submit for approval?', label: 'Submit', tone: 'primary', reason: false,
              msg: 'The Project Head and the Procurement Manager are told it is waiting.' },
  WITHDRAW: { title: 'Withdraw this payment?', label: 'Withdraw', tone: 'warning', reason: false,
              msg: 'It is set aside for good and never reduces the balance. Enter it again if it was right after all.' },
  APPROVE:  { title: 'Approve this payment?', label: 'Approve', tone: 'success', reason: false,
              msg: 'Open the screenshot first and check the amount, date and UTR against it. Once approved, the vendor\'s balance drops by this amount.' },
  RETURN:   { title: 'Return for correction?', label: 'Return', tone: 'warning', reason: true,
              msg: 'It goes back to the person who entered it, with your reason.' },
  REJECT:   { title: 'Reject this payment?', label: 'Reject', tone: 'danger', reason: true,
              msg: 'It is closed for good and never reduces the balance.' },
  REVERSE:  { title: 'Reverse this approved payment?', label: 'Reverse', tone: 'danger', reason: true,
              msg: 'The amount goes back onto the vendor\'s balance. The payment and your reason stay on the record; the correct one is then entered fresh.' },
}

export function OpeningPaymentDetail() {
  const { id = '' } = useParams()
  const { profile } = useAuth()
  const role = profile?.role
  const toast = useToast()
  const people = usePeople()
  const asAt = useOpeningAsAt()
  const mayView = can(role, 'viewOpeningLiabilities')
  const isMaker = can(role, 'makeOpeningLiability')
  const isChecker = can(role, 'checkOpeningLiability')
  const [acting, setActing] = useState<Act | null>(null)
  const [editing, setEditing] = useState(false)
  const [files, setFiles] = useState<PendingAttachment[]>([])
  const [filesKey, setFilesKey] = useState(0)

  const { data: rows, loading, refresh } = useQuery<any>(`v_liability_opening_payments:${id}`,
    () => supabase.from('v_liability_opening_payments').select('*').eq('id', id),
    [id], { cacheable: false, enabled: mayView })
  const p = rows[0]

  const { data: bal, refresh: refreshBal } = useQuery<any>(`v_liability_opening_by_vendor:${p?.opening_id ?? ''}`,
    () => supabase.from('v_liability_opening_by_vendor').select('*').eq('opening_id', p?.opening_id ?? ''),
    [p?.opening_id], { cacheable: false, idField: 'opening_id', enabled: mayView && !!p?.opening_id })
  const b = bal[0]

  // Every hook is above this line (scripts/check-hooks.py).
  if (!mayView) {
    return <NotPermitted what="see vendor opening liabilities"
                         who="Accounts, Procurement, the Project Head or the Board" />
  }
  if (loading) return <Loading />
  if (!p) return <EmptyState title={`Payment ${id} was not found`} />

  const mine = p.created_by === profile?.id
  const editable = ['DRAFT', 'RETURNED'].includes(p.status)
  const makerMay = isMaker && editable && (mine || role === 'admin')
  const checkerMay = isChecker && p.status === 'SUBMITTED' && !mine
  const act = acting ? ACT[acting] : null

  function reload() { refresh(); refreshBal() }

  return (
    <div className="space-y-4">
      <Link to="/opening-liabilities" className="inline-flex items-center gap-1 text-sm text-oval-700 hover:underline">
        <ArrowLeft className="h-4 w-4" /> Vendor Opening Liabilities
      </Link>

      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-slate-900">{p.id}</h1>
          <p className="text-sm text-slate-500">
            {rupees(p.amount)} to <strong>{p.vendor_name}</strong> ({p.vendor_id}) against the opening
            balance {asAt ? `as at ${ddmmyyyy(asAt)}` : ''}
          </p>
        </div>
        <StatusPill status={p.status} />
      </div>

      {p.status === 'RETURNED' && p.return_reason && (
        <Alert tone="warning" title="Returned for correction">{p.return_reason}</Alert>
      )}
      {p.status === 'REJECTED' && p.reject_reason && (
        <Alert tone="error" title="Rejected">{p.reject_reason}</Alert>
      )}
      {p.status === 'REVERSED' && (
        <Alert tone="error" title={`Reversed ${dateTime(p.reversed_at)} by ${people.name({ id: p.reversed_by })}`}>
          {p.reversal_reason}
        </Alert>
      )}
      {checkerMay && Number(p.attachment_count) === 0 && (
        <Alert tone="warning" title="No screenshot attached yet">
          It cannot be approved without one. If it was attached on a device that is offline, it
          uploads when that device reconnects; otherwise return it so the proof can be added.
        </Alert>
      )}
      {isChecker && p.status === 'SUBMITTED' && mine && (
        <Alert tone="info">You entered this payment, so another checker must approve it.</Alert>
      )}

      <div className="grid gap-4 lg:grid-cols-3">
        <Card title="The payment" className="lg:col-span-2">
          <dl className="grid gap-3 text-sm sm:grid-cols-2">
            <div><dt className="text-xs text-slate-500">Amount</dt><dd className="font-semibold">{rupees(p.amount)}</dd></div>
            <div><dt className="text-xs text-slate-500">Paid on</dt><dd>{ddmmyyyy(p.payment_date)}</dd></div>
            <div><dt className="text-xs text-slate-500">Mode</dt><dd>{p.mode}</dd></div>
            <div><dt className="text-xs text-slate-500">UTR / reference</dt><dd>{p.reference_no ?? '—'}</dd></div>
            <div><dt className="text-xs text-slate-500">Posted in</dt><dd>{ddmmyyyy(p.posting_date)}</dd></div>
            <div><dt className="text-xs text-slate-500">Entered by</dt>
              <dd>{people.name({ id: p.created_by })} · {dateTime(p.created_at)}</dd></div>
            {p.decided_by && (
              <div><dt className="text-xs text-slate-500">Decided by</dt>
                <dd>{people.name({ id: p.decided_by })} · {dateTime(p.decided_at)}</dd></div>
            )}
            {p.remarks && (
              <div className="sm:col-span-2"><dt className="text-xs text-slate-500">Remarks</dt><dd>{p.remarks}</dd></div>
            )}
          </dl>

          <div className="mt-4 flex flex-wrap gap-2">
            {makerMay && <>
              <button className="btn-secondary" onClick={() => setEditing(true)}>Correct it</button>
              <button className="btn-primary" onClick={() => setActing('SUBMIT')}>Submit for approval</button>
              <button className="btn-ghost" onClick={() => setActing('WITHDRAW')}>Withdraw</button>
            </>}
            {checkerMay && <>
              <button className="btn-success" onClick={() => setActing('APPROVE')}>Approve</button>
              <button className="btn-warning" onClick={() => setActing('RETURN')}>Return</button>
              <button className="btn-danger" onClick={() => setActing('REJECT')}>Reject</button>
            </>}
            {isChecker && p.status === 'APPROVED' && (
              <button className="btn-danger" onClick={() => setActing('REVERSE')}>Reverse</button>
            )}
          </div>
        </Card>

        <Card title="The balance it reduces" subtitle={b ? `Batch ${b.batch_id}` : undefined}>
          {!b ? <p className="text-sm text-slate-500">—</p> : (
            <dl className="space-y-2 text-sm">
              <div className="flex justify-between"><dt>Opening</dt><dd>{rupees(b.opening_amount)}</dd></div>
              <div className="flex justify-between"><dt>Paid (approved)</dt><dd>{rupees(b.paid_amount)}</dd></div>
              <div className="flex justify-between"><dt>Waiting for approval</dt><dd>{rupees(b.pending_amount)}</dd></div>
              <div className="flex justify-between border-t border-slate-200 pt-2 font-semibold">
                <dt>Still owed</dt><dd>{rupees(b.outstanding_amount)}</dd>
              </div>
              {p.status === 'SUBMITTED' && (
                <div className="flex justify-between text-xs text-slate-500">
                  <dt>After approving this</dt><dd>{rupees(Number(b.outstanding_amount) - Number(p.amount))}</dd>
                </div>
              )}
            </dl>
          )}
        </Card>
      </div>

      <AttachmentList key={filesKey} docType="OPENING_PAYMENT" docId={p.id} title="Payment screenshot"
                      subtitle="Open it and compare the amount, date and UTR before approving" />

      {makerMay && (
        <Card title="Add a screenshot">
          <AttachmentCollector docType="OPENING_PAYMENT" value={files} onChange={setFiles}
                               hint="Only if it is missing or the checker asked for a clearer one." />
          <button className="btn-secondary mt-3" disabled={files.length === 0}
                  onClick={async () => {
                    await queueAttachments('OPENING_PAYMENT', p.id, files)
                    setFiles([])
                    toast.success('Queued. It uploads straight away when you are online.')
                    setTimeout(() => { setFilesKey((k) => k + 1); refresh() }, 2500)
                  }}>
            Attach
          </button>
        </Card>
      )}

      <AuditTrail docType="OPENING_PAYMENT" docId={p.id} />

      {acting && act && (
        <Confirm open title={`${act.title} ${p.id}`}
                 message={<>{rupees(p.amount)} to {p.vendor_name}.<p className="mt-2">{act.msg}</p></>}
                 confirmLabel={act.label} tone={act.tone}
                 requireReason={act.reason} reasonLabel="Reason (at least 10 characters)"
                 onCancel={() => setActing(null)}
                 onConfirm={async (reason) => {
                   const { error } = acting === 'REVERSE'
                     ? await supabase.rpc('rpc_reverse_opening_payment', { p_id: p.id, p_reason: reason ?? '' })
                     : await supabase.rpc('rpc_opening_payment_action', {
                         p_id: p.id, p_action: acting, p_reason: reason ?? null,
                       })
                   if (error) { toast.error(dbMessage(error, 'Could not do that')); return }
                   toast.success(`${p.id}: done.`)
                   setActing(null)
                   reload()
                 }} />
      )}

      {editing && (
        <OpeningPaymentForm asAt={asAt} existing={p} onClose={() => setEditing(false)}
                            onDone={() => { setEditing(false); reload() }} />
      )}
    </div>
  )
}
